import React, { useState } from "react";
import { Button } from "../../components/ui/Button";
import { Sun, Info } from "lucide-react";
import ToggleSetting from "../../components/ui/ToggleSetting";
import SectionHeader from "../../components/ui/SectionHeader";

import { useOutletContext } from "react-router-dom";
import { SettingsOutletContextType } from "../Settings";
import { updateSunbedConfigs } from "../../services/settings";
import { useToast } from "../../hooks/useToast";
import GlobalLoader from "../../components/ui/GlobalLoader";
import PageHeader from "../../components/ui/PageHeader";

import { useAuth } from "../../auth/AuthContext";

const SunbedSettings = () => {
  const toast = useToast();
  const { user, updateUser } = useAuth(); // Access auth context
  const { settingsData, refreshSettings } = useOutletContext<SettingsOutletContextType>();
  const [enableSunbed, setEnableSunbed] = useState(true);
  const [enableSunbedTmax, setEnableSunbedTmax] = useState(true);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (settingsData && settingsData.settings) {
      setEnableSunbed(!!settingsData.settings.is_sunbed);
      setEnableSunbedTmax(!!settingsData.settings.is_sunbed_tmax);
    }
  }, [settingsData]);

  const handleSave = async () => {
    setSaving(true);

    try {
      const payload = {
        is_sunbed: (enableSunbed ? "active" : "inactive") as "active" | "inactive",
        is_sunbed_tmax: (enableSunbedTmax ? "active" : "inactive") as "active" | "inactive"
      };

      await updateSunbedConfigs(payload);

      // Instantly update global user state
      if (user && user.company) {
        updateUser({
          company: {
            ...user.company,
            ...payload
          }
        });
      }

      toast.success("Sunbed settings updated successfully!");
      refreshSettings();
    } catch (error: any) {
      console.error("Failed to update sunbed settings", error);
      toast.error("Failed to update sunbed settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!settingsData) {
    return (
      <div className="flex items-center justify-center p-12 min-h-[400px]">
        <GlobalLoader
          initialPageLoad={false}
          description="Loading sunbed settings..."
          compensateSidebar={false}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Sunbed Settings"
        description="Configure sunbed visibility and integration options"
        variant="card"
      />

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
        {/* Card Header */}
        <div className="bg-[#885ABB] px-6 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Sun size={18} className="text-white" />
            Sunbed Configuration
          </h3>
        </div>

        {/* Card Body */}
        <div className="p-5 flex-1">
          <div className="space-y-5">
            {/* Sunbed Settings Section */}
            <div>
              <SectionHeader title="Sunbed Settings" />

              {/* Info Box */}
              <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                <p className="text-xs text-purple-700 flex items-center gap-2">
                  <Info size={14} className="flex-shrink-0" />
                  Control sunbed visibility and TMAX integration for your
                  business
                </p>
              </div>

              <div className="space-y-4">
                {/* Sunbed Visibility */}
                <ToggleSetting
                  label="Sunbed Visibility"
                  description="Enable sunbed services for customers"
                  checked={enableSunbed}
                  onChange={setEnableSunbed}
                />

                {/* Sunbed Integration */}
                <ToggleSetting
                  label="Sunbed TMAX Integration"
                  description="Enable sunbed integration with TMAX system"
                  checked={enableSunbedTmax}
                  onChange={setEnableSunbedTmax}
                />
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-8 py-2 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SunbedSettings;
